import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, TextField, MenuItem,
  IconButton, Tooltip, CircularProgress, Divider, Paper, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress
} from '@mui/material';
import { Home, Save, Refresh, ScoreboardOutlined, Assessment, BarChart, People, Download, Upload, ArrowUpward, ArrowDownward, AutoFixHigh } from '@mui/icons-material';
import axios from 'axios';
import { useStages } from '../hooks/useStages';
import { canView } from '../utils/permissions';
import { computeScoreDoc } from '../utils/assessmentCalc';

const API = process.env.REACT_APP_API_URL;

const ScoreEntryPage = () => {
  const navigate = useNavigate();
  const { stages } = useStages();
  const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
  const isClassTeacher = role === 'classTeacher';
  // A subject teacher is a plain employee (role `user`) WITHOUT the academics
  // module — they reach Score Entry only for the subjects they're assigned, so
  // scope the dropdowns to their teaching scope just like a class teacher. A
  // role `user` who DOES hold the academics module (a data-entry clerk) keeps the
  // full, unscoped view.
  const isSubjectTeacher = role === 'user' && !canView('academics');
  // Any teacher (class teacher or assigned subject teacher) gets scoped dropdowns.
  const isTeacher = isClassTeacher || isSubjectTeacher;

  // A teacher's teaching scope: classes they class-teach (all subjects) + concrete
  // (class, subject) pairs they are allocated. Drives the dropdowns for teachers.
  const [teachScope, setTeachScope] = useState(null); // { classTeacherOf:[], subjectClasses:[] }

  const [filterY,    setFilterY]    = useState('');
  const [filterT,    setFilterT]    = useState('');
  const [filterS,    setFilterS]    = useState('');
  const [filterC,    setFilterC]    = useState('');
  const [filterLA,   setFilterLA]   = useState('');

  const [years,      setYears]      = useState([]);
  const [classes,    setClasses]    = useState([]);
  const [areas,      setAreas]      = useState([]);      // all learning areas (for name lookup)
  const [setupAreas, setSetupAreas] = useState([]);      // only areas with a setup for the selected grade/term/year
  const [setup,      setSetup]      = useState(null);
  const [selComp,    setSelComp]    = useState('');
  const [selExam,    setSelExam]    = useState('');   // examId of the selected paper ('' = legacy, no exam)
  const [exams,      setExams]      = useState([]);   // exams of the selected year/term/stage (weights for live grade)
  const [filterE,    setFilterE]    = useState('');   // exam being entered (picked BEFORE the subject)
  const [learners,   setLearners]   = useState([]);
  const [scores,     setScores]     = useState({}); // { admNo: scoreValue }
  const [existing,   setExisting]   = useState({}); // { admNo: scoreDoc }
  const [gradingScale, setGradingScale] = useState([]); // DB grading bands (8-level CBC)
  const [subjGradeSys, setSubjGradeSys] = useState({}); // { areaCode: gradingSystem } assigned under Stage Subjects

  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [regrading,  setRegrading]  = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [importError, setImportError] = useState('');  // shown inside the dialog
  const [importFile,  setImportFile]  = useState('');
  const [dragOver,    setDragOver]    = useState(false); // CSV drop-zone hover state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [sortBy, setSortBy] = useState('admNo'); // 'admNo' or 'name'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [showProgress, setShowProgress] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Grade on the SUBJECT's assigned grading system (Stage Subjects) when it has
  // one — so a Maths/Science exam previews on its own scale (e.g. 50% → C+) —
  // falling back to the stage's curriculum (CBC levels vs 8-4-4 letters) otherwise.
  // This mirrors what the server saves (systemForSubject), so the live grade and
  // the stored grade always agree. Refetched when the subject or stage changes.
  const selStageCurriculum = stages.find(s => s.code === filterS)?.curriculum || 'CBC';
  const selGradingSystem = subjGradeSys[filterLA] || selStageCurriculum;
  useEffect(() => {
    axios.get(`${API}/api/academics/grading-scale?system=${encodeURIComponent(selGradingSystem)}`, { headers })
      .then(r => setGradingScale(r.data || []))
      .catch(() => {});
  }, [selGradingSystem]); // eslint-disable-line

  // Map a percentage to its grade code via the DB scale (highest band first).
  // Round to a whole percent first — bands use integer boundaries, and a computed
  // percentage like 58/100*100 = 57.9999… would otherwise fall in the 57→58 gap.
  const resolveGrade = (pct) => {
    const p = Math.round(pct);
    const band = [...gradingScale]
      .sort((a, b) => b.minPercent - a.minPercent)
      .find(g => p >= g.minPercent && p <= g.maxPercent);
    return band?.code || '';
  };

  // Colour a grade chip from the band's own colour, falling back to the level prefix.
  const PREFIX_COLOR = { EE: '#2e7d32', ME: '#1565c0', AE: '#f57c00', BE: '#c62828' };
  const gradeColor = (code) => {
    const band = gradingScale.find(g => g.code === code);
    const bg = band?.color || PREFIX_COLOR[String(code).slice(0, 2)] || '#e0e0e0';
    return { bg, color: code ? '#fff' : '#555' };
  };

  // Re-stamp already-saved grades for the selected stage/year/term. The server
  // regrades each score on its subject's assigned grading system (Stage Subjects),
  // falling back to the stage curriculum — use after assigning/editing a subject's
  // grading system or a scale's bands so stored grades pick up the new boundaries.
  const handleRegrade = async () => {
    if (!filterS) { setError('Select a stage to regrade'); return; }
    setRegrading(true); setError('');
    try {
      const body = { stageCode: filterS };
      if (filterY) body.academicYear = filterY;
      if (filterT) body.term = filterT;
      const { data } = await axios.post(`${API}/api/academics/assessment-scores/regrade`, body, { headers });
      setSuccess(`Regraded ${data.updated} of ${data.scanned} saved score${data.scanned !== 1 ? 's' : ''} for ${filterS}`);
      loadExistingScores();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Regrade failed');
    } finally { setRegrading(false); }
  };

  // localStorage key for caching scores
  const getLocalStorageKey = () => {
    if (!filterY || !filterT || !filterS || !filterLA || !selComp) return null;
    return `scores_${filterY}_T${filterT}_${filterS}_${filterC || 'all'}_${filterLA}_${selExam ? selExam + '_' : ''}${selComp}`;
  };

  // Save scores to localStorage
  const saveScoresToCache = (scoresData) => {
    const key = getLocalStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(scoresData));
      setHasUnsavedChanges(Object.keys(scoresData).length > 0);
    } catch (err) {
      console.warn('Failed to save to localStorage:', err);
    }
  };

  // Load scores from localStorage
  const loadScoresFromCache = () => {
    const key = getLocalStorageKey();
    if (!key) return {};
    try {
      const cached = localStorage.getItem(key);
      return cached ? JSON.parse(cached) : {};
    } catch (err) {
      console.warn('Failed to load from localStorage:', err);
      return {};
    }
  };

  // Clear scores from localStorage
  const clearScoresCache = () => {
    const key = getLocalStorageKey();
    if (!key) return;
    try {
      localStorage.removeItem(key);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.warn('Failed to clear localStorage:', err);
    }
  };

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [yRes, aRes] = await Promise.all([
          axios.get(`${API}/api/academics/years`, { headers }),
          axios.get(`${API}/api/academics/learning-areas`, { headers }),
        ]);
        setYears(yRes.data);
        setAreas(aRes.data);
      } catch { /* silent */ }
    };
    loadMeta();
  }, []); // eslint-disable-line

  // Auto-fill the current year + term for everyone, and the stage + class for a
  // class teacher, so score entry opens ready to type marks.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/academics/current-period`, { headers });
        if (data.year) setFilterY(data.year);
        if (data.term) setFilterT(data.term);
      } catch { /* non-fatal */ }
      if (isTeacher) {
        try {
          const { data } = await axios.get(`${API}/api/academics/class-teacher/teaching-scope`, { headers });
          setTeachScope(data);
          const first = (data.classTeacherOf || [])[0] || (data.subjectClasses || [])[0];
          if (first) { setFilterS(first.stageCode); setFilterC(first.classCode); }
        } catch { /* non-fatal */ }
      }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!filterS) { setClasses([]); setFilterC(''); return; }
    // Teachers pick from their teaching scope (class-teacher classes + allocated
    // subject classes) for this stage; admins/staff pick any class in the stage.
    if (isTeacher) {
      const scoped = [
        ...((teachScope?.classTeacherOf) || []),
        ...((teachScope?.subjectClasses) || []),
      ].filter(c => c.stageCode === filterS);
      const dedup = Object.values(Object.fromEntries(scoped.map(c => [c.classCode, c])));
      setClasses(dedup);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/academics/classes?stageCode=${filterS}`, { headers });
        setClasses((data || []).filter(c => c.stageCode === filterS));
      } catch { setClasses([]); }
    })();
  }, [filterS, teachScope]); // eslint-disable-line

  // The Learning Area dropdown lists ONLY subjects that (a) are assigned to this
  // stage for the year (StageLearningArea) AND (b) have an assessment setup for the
  // selected grade/term/year. So a G10 list shows G10's subjects — not JSS subjects
  // or others that happen to have a stray setup — and non-examinable subjects
  // (e.g. Group Work) never appear since they can't be scored.
  useEffect(() => {
    if (!filterY || !filterT || !filterS) { setSetupAreas([]); return; }
    (async () => {
      try {
        const [setupRes, slaRes] = await Promise.all([
          axios.get(`${API}/api/academics/assessment-setups?academicYear=${filterY}&term=${filterT}&stageCode=${filterS}`, { headers }),
          axios.get(`${API}/api/academics/stage-learning-areas?year=${filterY}&stageCode=${filterS}`, { headers }).catch(() => ({ data: [] })),
        ]);
        // Subjects actually assigned to this stage this year (examinable only). If
        // the stage has no subjects configured, don't hide everything — fall back
        // to the setup list unfiltered (null = no restriction).
        const sla = slaRes.data || [];
        const stageCodes = sla.length
          ? new Set(sla.filter(a => a.isExaminable !== false).map(a => a.areaCode))
          : null;

        // Per-subject grading system (Stage Subjects). Empty string = "use the
        // stage curriculum", handled by the selGradingSystem fallback above.
        const gsMap = {};
        sla.forEach(a => { if (a.areaCode) gsMap[a.areaCode] = a.gradingSystem || ''; });
        setSubjGradeSys(gsMap);

        const seen = new Map();
        (setupRes.data || []).forEach(s => {
          if (stageCodes && !stageCodes.has(s.learningAreaCode)) return; // not a subject of this stage
          if (!seen.has(s.learningAreaCode)) {
            seen.set(s.learningAreaCode, {
              areaCode: s.learningAreaCode,
              examIds: [...new Set(s.components.map(c => String(c.examId || '')))],
              areaName: s.learningAreaName
                || areas.find(a => a.areaCode === s.learningAreaCode)?.areaName
                || s.learningAreaCode,
            });
          }
        });
        setSetupAreas([...seen.values()].sort((a, b) => a.areaName.localeCompare(b.areaName)));
        // Clear a stale selection if it no longer has a setup here.
        if (filterLA && !seen.has(filterLA)) setFilterLA('');
      } catch { setSetupAreas([]); }
    })();
  }, [filterY, filterT, filterS, areas]); // eslint-disable-line

  // Exams of the chosen year/term/stage. Pre-select the one used last time (else the
  // first by order) so entry starts with year → term → stage → subject → marks.
  useEffect(() => {
    if (!filterY || !filterT || !filterS) { setExams([]); setFilterE(''); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/academics/assessment-exams?academicYear=${filterY}&term=${filterT}&stageCode=${filterS}`, { headers });
        const list = data || [];
        setExams(list);
        let remembered = '';
        try { remembered = localStorage.getItem(`scoreEntryExam_${filterY}_T${filterT}_${filterS}`) || ''; } catch { /* storage unavailable */ }
        setFilterE(prev => (list.some(e => e._id === prev) ? prev
          : list.some(e => e._id === remembered) ? remembered : (list[0]?._id || '')));
      } catch { setExams([]); setFilterE(''); }
    })();
  }, [filterY, filterT, filterS]); // eslint-disable-line

  const pickExam = (id) => {
    setFilterE(id);
    try { localStorage.setItem(`scoreEntryExam_${filterY}_T${filterT}_${filterS}`, id); } catch { /* ignore */ }
  };

  const loadSetup = useCallback(async () => {
    if (!filterY || !filterT || !filterS || !filterLA) { setSetup(null); setSelComp(''); setSelExam(''); return; }
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(
        `${API}/api/academics/assessment-setups?academicYear=${filterY}&term=${filterT}&stageCode=${filterS}&learningAreaCode=${filterLA}`,
        { headers }
      );
      const found = data[0] || null;
      setSetup(found);
    } catch { setError('Failed to load assessment setup'); setSetup(null); }
    finally { setLoading(false); }
  }, [filterY, filterT, filterS, filterLA]); // eslint-disable-line

  useEffect(() => { loadSetup(); }, [loadSetup]);

  // Start on the first paper of the chosen exam; keep the current paper if it's
  // already in that exam (so switching subject within an exam is one click).
  useEffect(() => {
    if (!setup) return;
    const inExam = (c) => !filterE || String(c.examId || '') === filterE;
    const cur = setup.components.find(c => c.name === selComp && (c.examId || '') === (selExam || ''));
    if (cur && inExam(cur)) return;
    const first = setup.components.find(inExam) || setup.components[0];
    setSelComp(first?.name || '');
    setSelExam(first?.examId || '');
  }, [setup, filterE]); // eslint-disable-line

  // Stages a teacher can work in = the stages present in their teaching scope.
  const stageOptions = useMemo(() => {
    if (!isTeacher || !teachScope) return stages;
    const codes = new Set([
      ...((teachScope.classTeacherOf) || []).map(c => c.stageCode),
      ...((teachScope.subjectClasses) || []).map(c => c.stageCode),
    ]);
    return stages.filter(s => codes.has(s.code));
  }, [isTeacher, teachScope, stages]);

  // Subjects a teacher may enter for the chosen class: everything for a class they
  // class-teach, else only the subjects they're allocated. Admins/staff: all.
  // Subjects that have papers in the chosen exam (all subjects when no exam is in play).
  const examAreas = useMemo(() => (filterE
    ? setupAreas.filter(a => (a.examIds || []).includes(filterE))
    : setupAreas), [setupAreas, filterE]);

  const subjectOptions = useMemo(() => {
    if (!isTeacher || !teachScope) return examAreas;
    const isCT = (teachScope.classTeacherOf || []).some(c => c.classCode === filterC);
    const anyCTInStage = (teachScope.classTeacherOf || []).some(c => c.stageCode === filterS);
    if (isCT || (!filterC && anyCTInStage)) return examAreas;
    const allocAreas = new Set(
      (teachScope.subjectClasses || [])
        .filter(sc => (filterC ? sc.classCode === filterC : sc.stageCode === filterS))
        .map(sc => sc.areaCode));
    return examAreas.filter(a => allocAreas.has(a.areaCode));
  }, [isTeacher, teachScope, examAreas, filterC, filterS]);

  // Drop a selected subject that is no longer permitted for the chosen class.
  useEffect(() => {
    if (filterLA && !subjectOptions.some(a => a.areaCode === filterLA)) setFilterLA('');
  }, [subjectOptions]); // eslint-disable-line

  const loadLearners = useCallback(async () => {
    if (!filterY || !filterT || !filterS || !filterLA) { setLearners([]); return; }
    try {
      const params = new URLSearchParams({
        academicYear:     filterY,
        term:             filterT,
        stageCode:        filterS,
        learningAreaCode: filterLA
      });
      if (filterC) params.set('classCode', filterC);
      const { data } = await axios.get(`${API}/api/academics/learners?${params}`, { headers });
      setLearners(data);
    } catch { setLearners([]); }
  }, [filterY, filterT, filterS, filterC, filterLA]); // eslint-disable-line

  useEffect(() => { loadLearners(); }, [loadLearners]);

  // Warn user if they try to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes that are cached locally. They will be lost if you leave without saving to the server.';
        return 'You have unsaved changes that are cached locally. They will be lost if you leave without saving to the server.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // A score line belongs to a paper by (exam, name) — "PP1" can exist in several exams.
  const sameLine = (s) => s.componentName === selComp && (s.examId || '') === (selExam || '');

  const loadExistingScores = useCallback(async () => {
    if (!filterY || !filterT || !filterS || !filterLA || !selComp) return;
    try {
      const params = new URLSearchParams({ academicYear: filterY, term: filterT, stageCode: filterS, learningAreaCode: filterLA });
      if (filterC) params.set('classCode', filterC);
      const { data } = await axios.get(`${API}/api/academics/assessment-scores?${params}`, { headers });
      const map = {};
      const scoreMap = {};
      data.forEach(doc => {
        map[doc.learnerAdmNo] = doc;
        const line = doc.scores.find(sameLine);
        if (line && line.score !== null) scoreMap[doc.learnerAdmNo] = line.score;
      });
      setExisting(map);
      // Load cached scores and merge with posted scores (cached scores override)
      const cached = loadScoresFromCache();
      const merged = { ...scoreMap, ...cached };
      setScores(merged);
    } catch { /* silent */ }
  }, [filterY, filterT, filterS, filterLA, filterC, selComp, selExam]); // eslint-disable-line

  useEffect(() => { loadExistingScores(); }, [loadExistingScores]);

  const compDef = setup?.components?.find(c => c.name === selComp && (c.examId || '') === (selExam || ''));
  const maxScore = compDef?.maxScore || 0;

  const handleScore = (admNo, val) => {
    const n = val === '' ? '' : Math.min(parseFloat(val) || 0, maxScore);
    const updated = { ...scores, [admNo]: n === '' ? '' : n };
    setScores(updated);
    // Save to localStorage immediately
    saveScoresToCache(updated);
  };

  const liveGrade = (admNo) => {
    if (!setup || !selComp) return '';
    const raw = scores[admNo];
    if (raw === '' || raw === undefined) return '';
    const doc = existing[admNo];
    // Rebuild every paper's line (stored marks + the typed one), then apply the same
    // exam-weighting / paper-group maths the server uses, so the live grade matches.
    const lineOf = (c) => ({ componentName: c.name, examId: c.examId || null, examName: c.examName || '', group: c.group || '', score: null, maxScore: c.maxScore });
    const lines = setup.components.map(c => {
      const stored = doc?.scores?.find(s => s.componentName === c.name && (s.examId || '') === (c.examId || ''));
      return { ...lineOf(c), score: stored ? stored.score : null };
    });
    const idx = lines.findIndex(sameLine);
    if (idx >= 0) lines[idx] = { ...lines[idx], score: parseFloat(raw) || 0 };
    const calc = computeScoreDoc(lines, exams, setup.groups || []);
    if (!calc.totalMax || !calc.graded) return '';   // graded only once every paper of an exam is marked
    return resolveGrade(calc.percentage);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortedLearners = () => {
    const sorted = [...learners].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'admNo') {
        aVal = a.admNo;
        bVal = b.admNo;
        // Natural sort for admission numbers (handles numeric sorting)
        aVal = parseInt(aVal) || aVal;
        bVal = parseInt(bVal) || bVal;
      } else if (sortBy === 'name') {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleSave = async () => {
    if (!setup || !selComp) return;
    setSaving(true); setError('');
    // Save all learners with entered scores (including already-posted ones for editing)
    const entries = learners
      .filter(l => {
        if (scores[l.admNo] === '' || scores[l.admNo] === undefined) return false;
        return true;
      })
      .map(l => ({ admNo: l.admNo, score: parseFloat(scores[l.admNo]) }));
    if (entries.length === 0) { setError('No scores entered to save'); setSaving(false); return; }
    
    // Show progress dialog
    setShowProgress(true);
    setProgressTotal(entries.length);
    setProgressIndex(0);

    // Uniform progress animation: 0→95% over estimated time, then snap to 100% on complete
    const startTime = Date.now();
    const estimatedDuration = 3000; // Estimate 3 seconds for the request
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / estimatedDuration) * 95, 95); // Cap at 95%
      setProgressIndex(Math.floor(progress));
    }, 50); // Update every 50ms for smooth animation

    try {
      const { data } = await axios.post(`${API}/api/academics/assessment-scores/bulk`, {
        academicYear: filterY, term: parseInt(filterT, 10),
        stageCode: filterS, classCode: filterC || '',
        learningAreaCode: filterLA, componentName: selComp, examId: selExam || undefined, entries
      }, { headers });

      clearInterval(progressInterval);
      setProgressIndex(100); // Jump to complete

      const saved = data?.saved ?? 0;
      const errs  = data?.errors || [];

      // The endpoint returns 200 even when individual rows fail. Only clear the
      // local backup and reload once EVERY row is confirmed saved — otherwise the
      // entered scores would be wiped (this exact bug lost data before).
      if (saved < entries.length || errs.length) {
        const detail = errs[0]?.error ? ` — ${errs[0].error}` : '';
        setError(`Only ${saved} of ${entries.length} score(s) saved${detail}. Your entries are kept — fix the issue and Save again.`);
        setShowProgress(false);
        return; // keep cache + on-screen scores intact
      }

      clearScoresCache();
      setSuccess(`Saved ${saved} score(s)`);
      loadExistingScores();
      setTimeout(() => {
        setShowProgress(false);
      }, 1500);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      // Network / 5xx — keep everything so nothing is lost.
      clearInterval(progressInterval);
      setError(err.response?.data?.error || 'Save failed — your entries are kept locally. Try again.');
      setShowProgress(false);
    } finally { setSaving(false); }
  };

  const handleExport = () => {
    if (!setup || !selComp || learners.length === 0) return;
    const csvContent = [
      ['Adm No', 'Name', 'Score', 'Max Score'].join(','),
      ...learners.map(l => {
        const score = scores[l.admNo] ?? '';
        return [l.admNo, `"${l.name}"`, score, maxScore].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Scores_${compDef?.examName ? compDef.examName + '_' : ''}${selComp}_${filterY}_T${filterT}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Accept a file dropped onto the upload zone — same path as picking one, but
  // guard the type since a drop can be anything (an image, a folder, etc.).
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (importing) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setImportError('Please drop a .csv file.');
      return;
    }
    handleImport(file);
  };

  const handleImport = async (file) => {
    if (!file || !setup || !selComp) return;
    setImporting(true); setImportError(''); setImportFile(file.name);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) throw new Error('CSV must have header and data rows');

      const importedScores = {};
      const validationErrors = [];

      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(',');
        const admNo = parts[0]?.trim();
        const scoreStr = parts[2]?.trim();

        if (!admNo || scoreStr === undefined) continue;
        if (scoreStr === '') {
          // Allow blank scores for learners who did not sit; keep as empty string so
          // they are not posted to the server and remain editable in the UI.
          importedScores[admNo] = '';
          continue;
        }
        const score = parseFloat(scoreStr);
 
        // Validation
        if (isNaN(score)) {
          validationErrors.push(`Row ${i + 1}: Invalid score "${scoreStr}"`);
          continue;
        }
        if (score < 0) {
          validationErrors.push(`Row ${i + 1}: Negative score not allowed (${score})`);
          continue;
        }
        if (score > maxScore) {
          validationErrors.push(`Row ${i + 1}: Score ${score} exceeds max ${maxScore}`);
          continue;
        }
 
        importedScores[admNo] = score;
      }

      if (validationErrors.length > 0) {
        setImportError(`Validation failed:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n…and ${validationErrors.length - 5} more` : ''}`);
        setImporting(false);
        return;
      }

      const updated = { ...scores, ...importedScores };
      setScores(updated);
      saveScoresToCache(updated);
      setSuccess(`Imported ${Object.keys(importedScores).length} score(s)`);
      setImportOpen(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setImportError(`Import failed: ${err.message}`);
    } finally { setImporting(false); }
  };

  const laName = areas.find(a => a.areaCode === filterLA)?.areaName || filterLA;
  const className = classes.find(c => c.classCode === filterC)?.className || filterC;
  const selStage = stages.find(s => s.code === filterS);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 0 } }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <ScoreboardOutlined sx={{ color: '#283593' }} />
              <Typography variant="h5" sx={{ color: '#283593', fontWeight: 700 }}>Score Entry</Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              {filterS && (
                <Tooltip title={`Re-stamp saved grades for ${filterS} — each subject on its assigned grading system (falls back to the ${selStageCurriculum} stage scale)`}>
                  <span>
                    <Button size="small" variant="outlined" startIcon={<AutoFixHigh />}
                      onClick={handleRegrade} disabled={regrading}
                      sx={{ whiteSpace: 'nowrap' }}>
                      {regrading ? 'Regrading…' : 'Regrade'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Refresh scores"><IconButton onClick={loadExistingScores} sx={{ bgcolor: 'background.paper' }}><Refresh /></IconButton></Tooltip>
              <Tooltip title="Assessment Setup"><IconButton onClick={() => navigate('/academics/assessment-setup')} sx={{ bgcolor: '#e0f7fa' }}><Assessment sx={{ color: '#00838f' }} /></IconButton></Tooltip>
              <Tooltip title="Assessment Reports"><IconButton onClick={() => navigate('/academics/assessment-report')} sx={{ bgcolor: '#e0f2f1' }}><BarChart sx={{ color: '#004d40' }} /></IconButton></Tooltip>
              <Tooltip title="Learners List"><IconButton onClick={() => navigate('/learners')} sx={{ bgcolor: '#fbe9e7' }}><People sx={{ color: '#e65100' }} /></IconButton></Tooltip>
              <Tooltip title="Back to Academics"><IconButton onClick={() => navigate('/academics')} sx={{ bgcolor: 'background.paper' }}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
          {hasUnsavedChanges && <Alert severity="info" sx={{ mb: 2 }}>💾 Changes saved locally — click "Save All" to post to server</Alert>}

          {/* Selection panel */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Select Assessment</Typography>
            {/* Selects stack full-width on phones, sit inline on larger screens */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap',
              '& > .MuiFormControl-root': { flex: { xs: '1 1 100%', sm: '0 0 auto' } } }}>
              <TextField select label="Academic Year" size="small" value={filterY}
                onChange={e => setFilterY(e.target.value)} sx={{ minWidth: 140 }}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
              <TextField select label="Term" size="small" value={filterT}
                onChange={e => setFilterT(e.target.value)} sx={{ minWidth: 120 }}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {[1,2,3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
              </TextField>
              <TextField select label="Stage" size="small" value={filterS}
                onChange={e => { setFilterS(e.target.value); setFilterC(''); }} sx={{ minWidth: 130 }}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {stageOptions.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
              </TextField>
              <TextField select label="Class (optional)" size="small" value={filterC}
                onChange={e => setFilterC(e.target.value)} sx={{ minWidth: 150 }}>
                <MenuItem value=""><em>All classes</em></MenuItem>
                {classes.map(c => <MenuItem key={c.classCode} value={c.classCode}>{c.className}</MenuItem>)}
              </TextField>
              {exams.length > 0 && (
                <TextField select label="Exam" size="small" value={filterE}
                  onChange={e => pickExam(e.target.value)} sx={{ minWidth: 160 }}>
                  {exams.map(e => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
                </TextField>
              )}
              <TextField select label="Learning Area" size="small" value={filterLA}
                onChange={e => setFilterLA(e.target.value)} sx={{ minWidth: 200 }}
                disabled={!filterY || !filterT || !filterS}
                helperText={(filterY && filterT && filterS && examAreas.length === 0)
                  ? (filterE ? 'No subject has papers in this exam yet' : 'No assessment setup for this grade/term') : ''}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {subjectOptions.map(a => <MenuItem key={a.areaCode} value={a.areaCode}>{a.areaName}</MenuItem>)}
              </TextField>
            </Box>
          </Paper>

          {/* Setup info + component chips */}
          {loading && <Box display="flex" justifyContent="center" my={3}><CircularProgress /></Box>}

          {!loading && setup && (
            <>
              {/* Breadcrumb */}
              <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                <Chip label={selStage?.name || filterS} size="small" variant="outlined" />
                {filterC && <Chip label={className} size="small" variant="outlined" />}
                <Chip label={laName} size="small" color="primary" />
                <Chip label={`Term ${filterT}`} size="small" variant="outlined" />
                {!setup.isPublished && <Chip label="Draft — not published" size="small" color="warning" />}
              </Box>

              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Paper{filterE ? ` — ${exams.find(e => e._id === filterE)?.name || ''}` : ''}
              </Typography>
              <Box mb={3}>
                {(() => {
                  const groups = [];
                  setup.components.filter(c => !filterE || String(c.examId || '') === filterE).forEach(c => {
                    const k = c.examId || '';
                    let g = groups.find(x => x.k === k);
                    if (!g) { g = { k, name: c.examName || '', items: [] }; groups.push(g); }
                    g.items.push(c);
                  });
                  const seqOf = (k) => exams.find(e => e._id === k)?.sequence ?? 0;
                  groups.sort((a, b) => seqOf(a.k) - seqOf(b.k));
                  return groups.map(g => (
                    <Box key={g.k || 'none'} display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                      {g.name && <Typography variant="caption" fontWeight={700} sx={{ color: '#6a1b9a', minWidth: 80 }}>{g.name}</Typography>}
                      {g.items.map(c => {
                        const active = selComp === c.name && (selExam || '') === (c.examId || '');
                        return (
                          <Chip
                            key={`${c.examId || ''}|${c.name}`}
                            label={`${c.name}${c.group ? ' · ' + c.group : ''} (/${c.maxScore})`}
                            clickable
                            color={active ? 'primary' : 'default'}
                            variant={active ? 'filled' : 'outlined'}
                            onClick={() => { setSelComp(c.name); setSelExam(c.examId || ''); }}
                          />
                        );
                      })}
                    </Box>
                  ));
                })()}
              </Box>

              {selComp && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                    <Typography variant="subtitle2">
                      Entering: <strong>{compDef?.examName ? `${compDef.examName} · ` : ''}{selComp}</strong> — out of <strong>{maxScore}</strong>
                      <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                        ({learners.filter(l => {
                          const doc = existing[l.admNo];
                          const cl = doc?.scores?.find(sameLine);
                          return !(cl && cl.score !== null);
                        }).length} pending · {learners.filter(l => {
                          const doc = existing[l.admNo];
                          const cl = doc?.scores?.find(sameLine);
                          return cl && cl.score !== null;
                        }).length} posted)
                      </Typography>
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Export scores to CSV">
                        <Button
                          variant="outlined" size="small" startIcon={<Download />} onClick={handleExport}>
                          Export
                        </Button>
                      </Tooltip>
                      <Tooltip title="Import scores from CSV">
                        <Button
                          variant="outlined" size="small" startIcon={<Upload />}
                          onClick={() => { setImportError(''); setImportFile(''); setImportOpen(true); }}>
                          Import
                        </Button>
                      </Tooltip>
                      <Button
                        variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}
                        sx={{ bgcolor: '#283593', '&:hover': { bgcolor: '#1a237e' } }}>
                        {saving ? 'Saving…' : 'Save All'}
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ overflowX: 'auto', mx: { xs: -1.5, sm: 0 } }}>
                  <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell width={130} sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleSort('admNo')}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Adm. No.</strong>
                            {sortBy === 'admNo' && (sortOrder === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleSort('name')}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Learner Name</strong>
                            {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />)}
                          </Box>
                        </TableCell>
                        <TableCell align="center" width={120}><strong>Score /{maxScore}</strong></TableCell>
                        <TableCell align="center" width={100}><strong>Grade</strong></TableCell>
                        <TableCell align="center" width={90}><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {learners.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="text.secondary" py={2}>
                              No learners found for this selection.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : getSortedLearners().map(l => {
                        const doc      = existing[l.admNo];
                        const compLine = doc?.scores?.find(sameLine);
                        const isPosted = !!(compLine && compLine.score !== null);
                        const grade    = liveGrade(l.admNo);
                        const gc       = gradeColor(grade);
                        return (
                          <TableRow key={l.admNo} hover sx={{ bgcolor: isPosted ? '#f9fbe7' : 'inherit' }}>
                            <TableCell><Typography variant="caption">{l.admNo}</Typography></TableCell>
                            <TableCell>{l.name}</TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small" type="number" value={scores[l.admNo] ?? ''}
                                onChange={e => handleScore(l.admNo, e.target.value)}
                                disabled={doc?.isLocked || false}
                                inputProps={{ min: 0, max: maxScore, step: 0.5 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {grade ? (
                                <Chip label={grade} size="small" sx={{ bgcolor: gc.bg, color: gc.color, fontWeight: 700, fontSize: 11 }} />
                              ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                            </TableCell>
                            <TableCell align="center">
                              {doc?.isLocked
                                ? <Chip label="Locked" size="small" color="error" />
                                : isPosted
                                ? <Chip label="Posted" size="small" color="success" />
                                : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </Box>

                </>
              )}
            </>
          )}

          {!loading && filterY && filterT && filterS && filterLA && !setup && (
            <Alert severity="warning">
              No assessment setup found for this combination. Ask an admin to create one in{' '}
              <strong>Assessment Setup</strong> and publish it.
            </Alert>
          )}

          {!filterY || !filterT || !filterS || !filterLA ? (
            <Typography variant="body2" color="text.secondary" mt={2}>
              Select Academic Year, Term, Stage and Learning Area to load the assessment setup.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <Upload color="primary" /> Import Scores from CSV
        </DialogTitle>
        <DialogContent dividers>
          {/* In-dialog error — stays on top of the popup, never behind it */}
          {importError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setImportError('')}>
              {importError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Upload a CSV with columns: <strong>Adm No, Name, Score, Max Score</strong>.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Scores must not be negative or exceed the maximum ({maxScore}). Invalid rows are reported below.
          </Typography>

          {/* Clickable + drag-and-drop drop-zone upload area */}
          <Box
            component="label" htmlFor="import-file"
            onDragOver={e => { e.preventDefault(); if (!importing) setDragOver(true); }}
            onDragEnter={e => { e.preventDefault(); if (!importing) setDragOver(true); }}
            onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
            onDrop={handleDrop}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 1, py: 4, px: 2, textAlign: 'center', cursor: importing ? 'default' : 'pointer',
              border: '2px dashed',
              borderColor: importError ? 'error.main' : dragOver ? 'primary.main' : 'divider', borderRadius: 2,
              bgcolor: dragOver ? 'action.selected' : 'action.hover',
              transition: 'border-color .2s, background-color .2s',
              '&:hover': { borderColor: importing ? 'divider' : 'primary.main' },
            }}
          >
            {importing ? (
              <><CircularProgress size={26} /><Typography variant="body2" color="text.secondary">Importing {importFile}…</Typography></>
            ) : (
              <>
                <Upload sx={{ fontSize: 34, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={600}>
                  {importFile ? importFile : (dragOver ? 'Drop the CSV to import' : 'Drag & drop a CSV here, or click to choose')}
                </Typography>
                <Typography variant="caption" color="text.secondary">.csv only</Typography>
              </>
            )}
          </Box>
          <input
            type="file" accept=".csv" id="import-file" hidden disabled={importing}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = ''; }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Progress Dialog */}
      <Dialog open={showProgress} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <Save color="primary" /> Saving Scores
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Learning area info */}
            <Box>
              <Typography variant="caption" color="text.secondary">Learning Area</Typography>
              <Typography variant="body2" fontWeight={600}>{areas.find(a => a.areaCode === filterLA)?.areaName || filterLA}</Typography>
            </Box>
            
            {/* Score entry count */}
            <Box>
              <Typography variant="caption" color="text.secondary">Saving Scores</Typography>
              <Typography variant="body2" fontWeight={600}>{progressTotal} entries · {progressIndex}% complete</Typography>
            </Box>

            {/* Progress bar */}
            <Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(progressIndex, 100)}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                {Math.min(progressIndex, 100)}%
              </Typography>
            </Box>

            {/* Status message */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
              {progressIndex >= 95 ? '✓ Finishing…' : 'Processing...'}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScoreEntryPage;
